import { motion, AnimatePresence } from "framer-motion";
import { Plus, Check, Flame, AlertTriangle } from "lucide-react";

export const DessertConfirmModal = ({
  showDessertConfirm,
  setShowDessertConfirm,
  dessertIngredients,
  toggleDessertIngredient,
  newDessertIngredient,
  setNewDessertIngredient,
  addDessertIngredient,
  handleDessertConfirm,
  ingredients,
}) => {
  return (
    <AnimatePresence>
      {showDessertConfirm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-[150] p-0 sm:p-4"
          onClick={() => setShowDessertConfirm(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#FEFAE0] rounded-t-3xl sm:rounded-3xl max-w-lg w-full max-h-[90vh] sm:max-h-[85vh] flex flex-col shadow-2xl"
            data-testid="dessert-confirm-modal"
          >
            <div className="flex-shrink-0 bg-gradient-to-r from-[#E9C46A] to-[#F4A261] p-4 sm:p-5 rounded-t-3xl">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                  <span className="text-xl sm:text-2xl">{"\u{1F370}"}</span>
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-bold text-[#264653]" style={{ fontFamily: "'Fraunces', serif" }}>
                    Review Ingredients for Dessert
                  </h2>
                  <p className="text-[#264653]/70 text-xs sm:text-sm">Select ingredients suitable for your sweet treat</p>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 sm:p-5 min-h-0">
              <div className="bg-[#FFB703]/10 border border-[#FFB703]/30 rounded-2xl p-3 sm:p-4 mb-4 sm:mb-5">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#E76F51] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-[#264653]">
                      Your scanned image may contain vegetables or other ingredients not typically used in desserts.
                    </p>
                    <p className="text-xs text-[#264653]/60 mt-1">
                      Uncheck any ingredients you don't want in your dessert recipe.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-bold text-[#264653] mb-3">
                  Ingredients ({dessertIngredients.length} selected)
                </h3>
                
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newDessertIngredient}
                    onChange={(e) => setNewDessertIngredient(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDessertIngredient(); } }}
                    placeholder="Add ingredient (e.g., sugar, vanilla)"
                    className="flex-1 px-4 py-2.5 rounded-xl bg-white border-2 border-[#264653]/10 text-sm text-[#264653] placeholder:text-[#264653]/40 focus:border-[#E9C46A] focus:outline-none transition-colors"
                    data-testid="dessert-add-ingredient-input"
                  />
                  <button
                    onClick={addDessertIngredient}
                    disabled={!newDessertIngredient.trim()}
                    className="px-4 py-2.5 rounded-xl bg-[#E9C46A] text-[#264653] font-bold text-sm hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-1.5"
                    data-testid="dessert-add-ingredient-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Add
                  </button>
                </div>
                
                {dessertIngredients.length === 0 ? (
                  <p className="text-[#264653]/50 text-sm text-center py-4">No ingredients selected. Add some above!</p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {dessertIngredients.map((ingredient, index) => {
                      const isFromOriginal = ingredients.includes(ingredient);
                      return (
                        <button
                          key={index}
                          onClick={() => toggleDessertIngredient(ingredient)}
                          className="flex items-center gap-2 p-3 rounded-xl text-left transition-all bg-[#2A9D8F]/10 border-2 border-[#2A9D8F]"
                          data-testid={`dessert-ingredient-${index}`}
                        >
                          <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 bg-[#2A9D8F] text-white">
                            <Check className="w-3 h-3" />
                          </div>
                          <span className="text-sm font-medium capitalize text-[#264653] flex-1">{ingredient}</span>
                          {!isFromOriginal && (
                            <span className="text-[10px] px-1.5 py-0.5 bg-[#E9C46A]/30 text-[#264653]/70 rounded-full font-medium">Added</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {ingredients.filter(i => !dessertIngredients.includes(i)).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#264653]/10">
                    <p className="text-xs text-[#264653]/40 mb-2 font-medium">Removed from recipe:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {ingredients.filter(i => !dessertIngredients.includes(i)).map((ingredient, index) => (
                        <button
                          key={index}
                          onClick={() => toggleDessertIngredient(ingredient)}
                          className="px-2.5 py-1 rounded-lg bg-[#264653]/5 text-[#264653]/40 text-xs font-medium hover:bg-[#264653]/10 hover:text-[#264653]/60 transition-all line-through"
                        >
                          {ingredient}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-[#264653]/10">
                <p className="text-xs text-[#264653]/50">
                  {"\u{1F4A1}"} <span className="font-medium">Tip:</span> Common dessert ingredients include fruits, chocolate, cream, eggs, sugar, flour, butter, and vanilla.
                </p>
              </div>
            </div>

            <div className="flex-shrink-0 p-4 sm:p-5 border-t border-[#264653]/10 bg-white/80 backdrop-blur-sm flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 safe-area-bottom">
              <button
                onClick={() => setShowDessertConfirm(false)}
                className="flex-1 px-4 py-3 rounded-full border-2 border-[#264653]/20 text-[#264653] font-bold hover:bg-[#264653]/5 transition-all text-sm sm:text-base"
                data-testid="dessert-cancel-btn"
              >
                Go Back
              </button>
              <button
                onClick={handleDessertConfirm}
                disabled={dessertIngredients.length === 0}
                className="flex-1 px-4 py-3 rounded-full bg-[#E9C46A] text-[#264653] font-bold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
                style={{ boxShadow: "3px 3px 0px 0px rgba(38, 70, 83, 0.8)" }}
                data-testid="dessert-confirm-btn"
              >
                <Flame className="w-5 h-5" />
                Confirm & Generate ({dessertIngredients.length})
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
